/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Producto } from 'src/app/models/Producto';
import { PostItem } from 'src/app/models/PostItem';
import { PostPedido, TotalSizes } from 'src/app/models/PostPedido';
import { Sucursal } from 'src/app/models/Sucursal';
import { User } from 'src/app/models/User.model';
import { FormProductos, PedidoForm } from './pedido-state.service';

@Injectable({ providedIn: 'root' })
export class PedidoTotalsService {

  getTotalesFromDetalles(detalles: FormProductos) {
    return {
      importe_descuento: detalles.dto1_value + detalles.dto2_value,
      importe_exento: 0,
      importe_impuestos_internos: 0,
      importe_impuestos_internos_con_descuento: 0,
      importe_iva: detalles.iva_value,
      importe_neto_gravado: detalles.bruto,
      importe_neto_gravado_con_descuento:
        detalles.bruto - detalles.dto1_value - detalles.dto2_value,
      importe_neto_nogravado: detalles.iva_value,
      importe_percepciones_iibb: 0,
      importe_subtotal_con_descuento:
        detalles.subtotal - detalles.dto1_value - detalles.dto2_value,
      importe_subtotal_sin_descuento: detalles.subtotal,
      importe_total: detalles.subtotal,
      subtotal_impuestos_internos: 0,
    };
  }

  getItemForPost(
    detalle: FormProductos,
    productoCargado: Producto,
    detProductoCargado: FormProductos
  ): PostItem {
    const precio = detalle.precioModel || detProductoCargado.precioModel;
    // iva_unitario es UNITARIO (sobre precio de lista, sin descuentos):
    // detalle.bruto es total de linea (precio_unidad x cantidad), usar precio_unidad.
    const iva_unitario =
      (parseFloat(detalle.precio_unidad?.toString() || '0') *
        parseFloat(detalle.iva)) /
      100;
    const descuento_2_unitario = (detalle.dto2_value || 0) / detalle.cantidad;
    const descuento_1_unitario = (detalle.dto1_value || 0) / detalle.cantidad;
    // costo_unitario_con_descuento = costo del producto - descuentos en cascada
    // (misma convencion que front web: no es precio+iva, son totales de linea).
    const costo = parseFloat(
      productoCargado.costo_unitario_max?.toString() || '0'
    );
    const desc_costo_1 =
      (costo * parseFloat(detalle.dto1?.toString() || '0')) / 100;
    const desc_costo_2 =
      ((costo - desc_costo_1) * parseFloat(detalle.dto2?.toString() || '0')) /
      100;
    const costo_unitario_con_descuento = costo - desc_costo_1 - desc_costo_2;
    return {
      cantidad: parseFloat(detalle.cantidad?.toString() || '0'),
      costo_unitario_con_descuento,
      costo_unitario_max: 0,
      custom: false,
      descuento_1_unitario,
      descuento_2_unitario,
      editando: false,
      impuesto_interno_alicuota: 0,
      impuesto_interno_unitario: 0,
      impuesto_interno_unitario_bruto: 0,
      precio,
      iva: productoCargado.iva,
      iva_id: productoCargado.iva?.id,
      lista_precio_id: detalle.precioModel.lista_id || precio.lista_id,
      iva_unitario,
      iva_unitario_bruto: detalle.iva_value / detalle.cantidad,
      porcentaje_descuento_1: parseFloat(detalle.dto1?.toString() || '0'),
      porcentaje_descuento_2: parseFloat(detalle.dto2?.toString() || '0'),
      precio_custom: false,
      precio_unidad: parseFloat(detalle.precio_unidad?.toString() || '0'),
      precio_unitario: parseFloat(detalle.precio_unidad?.toString() || '0'),
      precio_unitario_bruto:
        (detalle.subtotal - detalle.iva_value) / detalle.cantidad,
      precio_unitario_neto: detalle.subtotal / detalle.cantidad,
      unidad: detalle.unidadModel,
      producto: productoCargado,
      producto_id: productoCargado.id,
      subtotal_bruto: detalle.subtotal - detalle.iva_value,
      subtotal_bruto_con_impuestos: detalle.subtotal - detalle.iva_value,
      subtotal_descuento: parseFloat(detalle.dto1_value?.toString() || '0'),
      subtotal_descuento2: parseFloat(detalle.dto2_value?.toString() || '0'),
      subtotal_descuentos:
        parseFloat(detalle.dto1_value?.toString() || '0') +
        parseFloat(detalle.dto2_value?.toString() || '0'),
      subtotal_impuestos_internos: 0,
      subtotal_iva: parseFloat(detalle.iva_value?.toString() || '0'),
      subtotal_neto: parseFloat(detalle.subtotal?.toString() || '0'),
      subtotal_unitario:
        detalle.subtotal -
        (detalle.dto1_value || 0) -
        (detalle.dto2_value || 0),
    };
  }

  buildPostPedido(
    pedidoForm: PedidoForm,
    detalles: FormProductos[],
    productos: Producto[],
    user: User,
    editPedidoId?: number,
    onlySave?: boolean
  ): PostPedido {
    const sizes: TotalSizes = {} as TotalSizes;
    let importe_gravado = 0;
    let importe_gravado_con_descuento = 0;
    let importe_nogravado = 0;
    let importe_nogravado_con_descuento = 0;
    const items = detalles?.map((detalle, index) => {
      const item = this.getItemForPost(detalle, productos[index], detalles[index]);
      if (detalle.unidadModel.fraccionable) {
        sizes.total_pesable =
          parseFloat(sizes.total_pesable?.toString() || '0') +
          parseFloat(detalle.cantidad?.toString() || '0');
      } else if (detalle.unidadModel.es_bulto) {
        sizes.total_bultos =
          parseFloat(sizes.total_bultos?.toString() || '0') +
          parseFloat(detalle.cantidad?.toString() || '0');
      } else {
        sizes.total_unidades =
          parseFloat(sizes.total_unidades?.toString() || '0') +
          parseFloat(detalle.cantidad?.toString() || '0');
      }

      if (detalle.iva_value) {
        importe_gravado +=
          detalle.bruto - (detalle.dto1_value || 0) - (detalle.dto2_value || 0);
        importe_gravado_con_descuento +=
          detalle.bruto - (detalle.dto1_value || 0) - (detalle.dto2_value || 0);
      } else {
        importe_nogravado +=
          detalle.bruto - (detalle.dto1_value || 0) - (detalle.dto2_value || 0);
        importe_nogravado_con_descuento +=
          detalle.bruto - (detalle.dto1_value || 0) - (detalle.dto2_value || 0);
      }
      return item;
    });
    return {
      id: editPedidoId,
      abbr_comprobante: 'PED',
      accion_pedido: onlySave ? 'guardar' : 'finalizar',
      cliente_id: pedidoForm.cliente.id,
      concepto_id: 1,
      condicion_venta:
        pedidoForm.detalles_cliente.cond_venta === 'Contado'
          ? 'contado'
          : 'cta_cte',
      tipo_emision: 'PEDIDO',
      entrega: {
        horario: pedidoForm.detalles_cliente.horario || '',
        domicilio: pedidoForm?.detalles_cliente?.domicilio || '',
        piso:
          pedidoForm?.detalles_cliente?.piso?.toString() ||
          pedidoForm?.cliente?.domicilio_fiscal_piso,
        departamento:
          pedidoForm?.detalles_cliente?.dpto?.toString() ||
          pedidoForm?.cliente?.domicilio_fiscal_dpto,
        latlong: '',
        telefono:
          pedidoForm.detalles_cliente.telefono || pedidoForm.cliente.telefono,
        localidad: pedidoForm?.detalles_cliente?.localidad || '',
        codigo_postal: pedidoForm?.detalles_cliente?.codigoPostal || '',
        provincia_id: pedidoForm?.detalles_cliente?.provincia_id || null,
      },
      vendedor_id: user.id,
      importe_descuento_bruto: 0,
      importe_descuento_neto: 0,
      importe_exento: 0,
      importe_final: pedidoForm.total,
      importe_gravado,
      importe_gravado_con_descuento,
      importe_impuestos_internos: 0,
      importe_impuestos_internos_con_descuento: 0,
      importe_iva: pedidoForm.ivaValor,
      importe_iva_con_descuento: pedidoForm.ivaValor,
      importe_nogravado,
      importe_nogravado_con_descuento,
      importe_otros_tributos: 0,
      importe_recargo_bruto: 0,
      importe_recargo_neto: 0,
      importe_subtotal_bruto: pedidoForm.bruto - (pedidoForm.descuentos || 0),
      importe_subtotal_bruto_con_descuento:
        pedidoForm.bruto - (pedidoForm.descuentos || 0),
      importe_subtotal_neto: pedidoForm.total,
      importe_subtotal_neto_con_descuento: pedidoForm.total,
      fecha_comprobante: new Date()
        .toISOString()
        .split('T')[0]
        .split('-')
        .reverse()
        .join('/'),
      fecha_vencimiento: null,
      email: pedidoForm?.detalles_cliente?.email || pedidoForm.cliente.email_contacto || pedidoForm.cliente.email_facturacion || '',
      enviar_email: pedidoForm.detalles_cliente.to_mail ? '1' : '0',
      forma_entrega_id:
        pedidoForm.detalles_cliente.forma_entrega === 'Por Repartidor' ? 2 : 1,
      total_bultos: sizes.total_bultos || 0,
      total_pagado: 0,
      total_pesable: sizes.total_pesable || 0,
      total_unidades: sizes.total_unidades || 0,
      tipo_comprobante: {
        id: 1,
        titulo: 'Pedido A',
        abbr: 'FAC',
        letra: 'A',
        tipo: 'debito',
        estado: 1,
        codigo_afip: '001',
      },
      items,
      total_items: detalles?.length,
      sucursal_id: user.sucursal_default.id,
      observaciones: pedidoForm.observaciones,
    };
  }
}
